import { getFirestore, getCloudStorage, getArrayRemove, getArrayUnion, getFirestoreIncrementField, getFirestoreDeleteField } from '../../system/firebase';

import {
    SNACK_BAR_MESSAGE_OPERATION_FAILED,
    SNACK_BAR_MESSAGE_INTERVIEW_SUCCESS,
    SNACK_BAR_MESSAGE_SAVE_TO_MY_JOBS,
    SNACK_BAR_MESSAGE_REMOVE_FROM_MY_JOBS,
    SNACK_BAR_MESSAGE_UPDATE_MY_JOBS,
    SNACK_BAR_MESSAGE_SEND_FAILED,
    INTERVIEW_STATUS_PENDING,
    SNACK_BAR_MESSAGE_UPDATE_INTERVIEW_SUCCESS,
} from '../constants';
import { convertFirebaseTimestampsToDate, getCombinedDateTime } from '../dateHelper';
import { mapDefinedFields, deleteEmptyFields, removeFromDto } from '../dtoHelper';
import { getNameByUser } from '../textHelper';
import { nanoid } from '../../common/nanoidHelper';
import { getTotalEngagementMax } from '../employerHelper';
import { COMPOSITE_DOC_ID_DELIMITER } from '../../system/constants';

import { addDays, format as formatDate } from 'date-fns';

import _get from 'lodash/get';
import _set from 'lodash/set';
import _forEach from 'lodash/forEach';
import _some from 'lodash/some';
import _map from 'lodash/map';
import _noop from 'lodash/noop';
import _includes from 'lodash/includes';
import _keys from 'lodash/keys';

export const loadProfile = (employerId, set) => {
    const db = getFirestore();
    const profileEmpRef = db.collection('profilesEmp').doc(employerId);

    profileEmpRef.onSnapshot(docSnapshot => {
        set(docSnapshot.data());
    });
};

export const requestEngagement = async ({ employerId, jobSeekerId, jobId, jsEngageCount, jsEngageMax, setIsOkToSave, setLimitModalOpen, setIsNewEngagement }) => {
    const db = getFirestore();

    let isNewEngagement = true;

    const query = db.collection(`myJobsEmp/${employerId}/engagements`).where('jobId', '==', jobId).where('jsId', '==', jobSeekerId).limit(1);

    try {
        const snapshot = await query.get();
        snapshot.forEach((/* doc */) => {
            isNewEngagement = false;
        });

        setIsNewEngagement(isNewEngagement);

        // if no limit or limit not reached or limit reached but js engaged for this job listing before, allow save.
        if (jsEngageMax === undefined || jsEngageCount < jsEngageMax || !isNewEngagement) {
            setIsOkToSave(true);
        } else {
            setLimitModalOpen(true);
        }
    } catch (error) {
        console.log('Failed to obtain information about job seeker engagement', error);
    }
};

/**
 * To remove saved job seeker, call saveJobSeekerToJobs() but pass in jobIdsToUnSave array (just one jobId in this case)
 *   and pass in jobSeekerData (only needs userId in this case).
 *
 * @param {*} param0
 */
export const deleteApplicantsFromMyJobs = ({ employerId, jobSeekerData = {}, jobId, setPopoverOpen, setSnackbarMessage, setSnackbarErrorMessage, callback }) => {
    const jobsToUnSave = [{ jobId }];
    saveJobSeekerToJobs({ isRemoveApplicant: true, employerId, jobSeekerData, jobsToUnSave, setPopoverOpen, setSnackbarMessage, setSnackbarErrorMessage, callback });
};

/**
 * To remove saved job seeker, call saveJobSeekerToJobs() but pass in jobIdsToUnSave array (just one jobId in this case)
 *   and pass in jobSeekerData (only needs userId in this case).
 *
 * @param {*} param0
 */
export const deleteSavedJobSeekerFromMyJobs = async ({ employerId, jobSeekerData = {}, jobId, setPopoverOpen, setSnackbarMessage, setSnackbarErrorMessage, callback }) => {
    const jobsToUnSave = [{ jobId }];
    await saveJobSeekerToJobs({ employerId, jobSeekerData, jobsToUnSave, setPopoverOpen, setSnackbarMessage, setSnackbarErrorMessage, callback });
};

export const saveJobSeekerToJobs = async ({
    isRemoveApplicant = false,
    employerId,
    jobSeekerData = {},
    jobsToSave = [],
    jobsToUnSave = [],
    callback,
    setSnackbarMessage,
    setSnackbarErrorMessage,
}) => {
    const db = getFirestore();
    const now = new Date();

    //-------------------------------------------------------------------------------------------
    // Obtain job seeker data
    //-------------------------------------------------------------------------------------------
    const { userId: jsUserId, ...otherJsData } = jobSeekerData;
    const jsDto = mapDefinedFields({ ...otherJsData, savedDt: now });

    //-------------------------------------------------------------------------------------------
    // prep for savedJobSeekers
    //-------------------------------------------------------------------------------------------
    const savedJobSeekersRef = db.collection('savedJobSeekers');

    //-------------------------------------------------------------------------------------------
    // get a list of doc id's for the savedJobSeekers to be removed
    //-------------------------------------------------------------------------------------------
    const deleteSavedJobSeekerDocIdList = [];
    await Promise.all(
        _map(jobsToUnSave, async job => {
            const jobId = _get(job, 'jobId');
            const savedJobSeekersQuerySnapshot = await savedJobSeekersRef.where('jsId', '==', jsUserId).where('jobId', '==', jobId).get();
            savedJobSeekersQuerySnapshot.forEach(savedJobSeekerDoc => {
                deleteSavedJobSeekerDocIdList.push(savedJobSeekerDoc.id);
            });
        }),
    );

    //-------------------------------------------------------------------------------------------
    // prep for myJobsEmp
    //-------------------------------------------------------------------------------------------
    const empJobsRef = db.collection(`myJobsEmp/${employerId}/empJobs`);

    // iterate the save list and add to savedJs
    const empJobDtos = {};
    _forEach(jobsToSave, job => {
        const jobId = _get(job, 'jobId');
        _set(empJobDtos, `${jobId}.jobSeekers.${jsUserId}`, jsDto);
        _set(empJobDtos, `${jobId}.savedJs`, getArrayUnion(jsUserId));
    });

    // iterate the un-save lists and remove from savedJs
    _forEach(jobsToUnSave, job => {
        const jobId = _get(job, 'jobId');
        // DTO to update savedJs and jobSeekers in myJobsEmp.empJobs
        if (isRemoveApplicant) {
            _set(empJobDtos, `${jobId}.applicants`, getArrayRemove(jsUserId));
        } else {
            _set(empJobDtos, `${jobId}.savedJs`, getArrayRemove(jsUserId));
        }
    });

    // get all the empJobId's
    const empJobIds = _keys(empJobDtos);

    try {
        await db.runTransaction(async transaction => {
            //-------------------------------------------------------------------------------------------
            // read myJobsEmp
            //-------------------------------------------------------------------------------------------
            // for the jobs to be removed, see if we need to remove the entry from jobSeekers
            await Promise.all(
                _map(empJobIds, async empJobId => {
                    const empJobDocRef = empJobsRef.doc(empJobId);
                    const empJobDoc = await transaction.get(empJobDocRef);
                    const empJobData = empJobDoc.data();
                    const savedJs = _get(empJobData, 'savedJs', []);
                    const applicants = _get(empJobData, 'applicants', []);

                    _forEach(jobsToUnSave, o => {
                        const jobId = o.jobId;

                        if ((isRemoveApplicant && !_includes(savedJs, jsUserId)) || (!isRemoveApplicant && !_includes(applicants, jsUserId))) {
                            _set(empJobDtos, `${jobId}.jobSeekers.${jsUserId}`, getFirestoreDeleteField());
                        }
                    });
                }),
            );

            //-------------------------------------------------------------------------------------------
            // write savedJobSeekers
            //-------------------------------------------------------------------------------------------
            await Promise.all(
                _map(jobsToSave, async job => {
                    const savedJobSeekerRef = savedJobSeekersRef.doc(nanoid());
                    const savedJobSeekerDto = {
                        jsId: jsUserId,
                        jobId: _get(job, 'jobId'),
                        empId: employerId,
                        jobTitle: _get(job, 'jobTitle'),
                        country: _get(job, 'country'),
                        state: _get(job, 'state'),
                        city: _get(job, 'city'),
                        createDt: now,
                    };
                    transaction.set(savedJobSeekerRef, savedJobSeekerDto);
                }),
            );

            await Promise.all(
                _map(deleteSavedJobSeekerDocIdList, async savedJobSeekerDocId => {
                    transaction.delete(savedJobSeekersRef.doc(savedJobSeekerDocId));
                }),
            );

            //-------------------------------------------------------------------------------------------
            // write myJobsEmp empJobs
            //-------------------------------------------------------------------------------------------
            await Promise.all(
                _map(empJobIds, async empJobId => {
                    const empJobRef = empJobsRef.doc(empJobId);
                    transaction.set(empJobRef, empJobDtos[empJobId], { merge: true });
                }),
            );
        });

        callback && callback();

        const saveSuccessfulMessage =
            jobsToSave.length > 0 && jobsToUnSave.length > 0
                ? SNACK_BAR_MESSAGE_UPDATE_MY_JOBS
                : jobsToSave.length > 0
                ? SNACK_BAR_MESSAGE_SAVE_TO_MY_JOBS
                : jobsToUnSave.length > 0
                ? SNACK_BAR_MESSAGE_REMOVE_FROM_MY_JOBS
                : null;
        if (saveSuccessfulMessage) {
            setSnackbarMessage(saveSuccessfulMessage);
        }
    } catch (error) {
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        console.error(error);
    }
};

const assignInterviewerIds = ({ interviewers = [] }) => {
    return _map(interviewers, item => (item.id ? item : { ...item, id: nanoid(6) }));
};

export const updateInterview = async ({ data, setSnackbarMessage, setSnackbarErrorMessage, postSaveActions = _noop }) => {
    const db = getFirestore();

    //-------------------------------------------------------------------------------------------
    // obtain data
    //-------------------------------------------------------------------------------------------
    const { virtual, interviewers = [], externalInterviewLink } = data;
    let payload = {
        ...data,
        startDt: getCombinedDateTime(data.date, data.startTime),
        endDt: getCombinedDateTime(data.date, data.endTime),
        updateDt: new Date(),
        interviewers: assignInterviewerIds({ interviewers }),
    };
    delete payload.startTime;
    delete payload.endTime;
    delete payload.date;
    delete payload.interviewId;
    if (virtual) {
        payload = {
            ...payload,
            streetAddress1: getFirestoreDeleteField(),
            streetAddress2: getFirestoreDeleteField(),
            postalCode: getFirestoreDeleteField(),
            state: getFirestoreDeleteField(),
            country: getFirestoreDeleteField(),
            city: getFirestoreDeleteField(),
            cityLocation: getFirestoreDeleteField(),
        };
    }

    //-------------------------------------------------------------------------------------------
    // prep for interviews
    //-------------------------------------------------------------------------------------------
    const interviewRef = db.collection('interviews').doc(data.interviewId);

    try {
        await db.runTransaction(async transaction => {
            //-------------------------------------------------------------------------------------------
            // read interviews
            //-------------------------------------------------------------------------------------------
            const interviewDoc = await transaction.get(interviewRef);
            if (!interviewDoc.exists) throw new Error('The interview document not exists');
            const interviewData = interviewDoc.data();
            const existingJoinCode = _get(interviewData, 'joinCode');

            if (interviewers.length === 0) {
                // no more interviewers, remove existing joinCode and interviewers
                payload = { ...payload, joinCode: getFirestoreDeleteField(), interviewers: getFirestoreDeleteField() };
            } else if (!existingJoinCode) {
                // there are interviewers but no joinCode (should never happen unless there is a bug), generate one
                payload = { ...payload, joinCode: nanoid(10) };
            }

            //-------------------------------------------------------------------------------------------
            // write interviews
            //-------------------------------------------------------------------------------------------
            await transaction.set(interviewRef, mapDefinedFields(payload), { merge: true });
        });

        //-------------------------------------------------------------------------------------------
        // successful
        //-------------------------------------------------------------------------------------------
        postSaveActions();
        setSnackbarMessage(SNACK_BAR_MESSAGE_UPDATE_INTERVIEW_SUCCESS);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const saveNewInterview = async ({
    jobId,
    date: interviewDate,
    jobTitle,
    pocNames,
    startTime,
    endTime,
    virtual,
    streetAddress1,
    streetAddress2,
    city,
    cityLocation,
    state,
    country,
    postalCode,
    externalInterviewLink,
    interviewers: interviewerList,
    interviewersInvitedBy,
    ////
    // isNewEngagement,
    activeJobMax,
    jsEngageMax,
    jobSeekerId,
    jobSeekerName,
    employerId,
    companyName,
    // isEmpBadge,
    // tier,
    setSnackbarMessage,
    setSnackbarErrorMessage,
    callback = _noop,
}) => {
    console.log({ interviewersInvitedBy });
    const db = getFirestore();

    //-------------------------------------------------------------------------------------------
    // obtain data
    //-------------------------------------------------------------------------------------------
    // const { companyName, isEmpBadge, tier } = employer;
    const now = new Date();
    let joinCode = null;

    // save interviewer only if both name and email are provided
    const interviewers = [];
    _forEach(interviewerList, interviewer => {
        if (interviewer?.name && interviewer?.email) {
            interviewers.push({ ...interviewer });
        }
    });

    //-------------------------------------------------------------------------------------------
    // prep for users
    //-------------------------------------------------------------------------------------------
    const jsUserRef = db.collection('users').doc(jobSeekerId);

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);

    //-------------------------------------------------------------------------------------------
    // prep for interviews
    //-------------------------------------------------------------------------------------------
    const interviewId = nanoid();
    const interviewRef = db.collection('interviews').doc(interviewId);
    if (interviewers?.length > 0) {
        joinCode = nanoid(10);
    }
    console.log({ joinCode });

    //-------------------------------------------------------------------------------------------
    // prep for myJobsEmp
    //-------------------------------------------------------------------------------------------
    const myJobsEmpRef = db.collection('myJobsEmp').doc(employerId);
    // const empJobDocRef = myJobsEmpRef.collection('empJobs').doc(jobId);

    //-------------------------------------------------------------------------------------------
    // prep for engagements (existing JS)
    //-------------------------------------------------------------------------------------------
    const jsEngagementsQuery = db.collection(`myJobsEmp/${employerId}/engagements`).where('jsId', '==', jobSeekerId).limit(1);

    //-------------------------------------------------------------------------------------------
    // read engagements (existing JS)
    //-------------------------------------------------------------------------------------------
    const jsEngagementsSnapshot = await jsEngagementsQuery.get();
    const isNewEngagement = jsEngagementsSnapshot.size === 0;

    //-------------------------------------------------------------------------------------------
    // start transaction
    //-------------------------------------------------------------------------------------------
    try {
        await db.runTransaction(async transaction => {
            //-------------------------------------------------------------------------------------------
            // read employers
            //-------------------------------------------------------------------------------------------
            const employerDoc = await transaction.get(employerRef);
            if (!employerDoc.exists) throw new Error('The employer document not exists');
            const employerData = employerDoc.data();
            const tier = employerData.tier;
            const billMonthly = employerData.billMonthly;

            // for new engagement, check if employer has already reached the billing cycle limit
            if (isNewEngagement && (tier === 1 || tier === 2)) {
                const jsEngageCount = employerData.jsEngageCount;
                const jsEngageLimit = getTotalEngagementMax({ tier, jsEngageMax, activeJobMax, billMonthly });
                if (jsEngageCount >= jsEngageLimit) {
                    throw new Error('engagement-limit-exceeded');
                }
            }

            //-------------------------------------------------------------------------------------------
            // read users
            //-------------------------------------------------------------------------------------------
            const jsUserDoc = await transaction.get(jsUserRef);
            if (!jsUserDoc.exists) throw new Error('The user document not exists');
            const jsName = getNameByUser(jsUserDoc.data());

            //-------------------------------------------------------------------------------------------
            // read employers
            //-------------------------------------------------------------------------------------------
            // const employerDoc = await transaction.get(employerRef);
            // const employerData = employerDoc.data();

            // // record this engagement if not already exist
            // const jsEngagedList = _get(employerData, 'jsEngagedList', []);
            // if (!_some(jsEngagedList, o => o.jobId === jobId && o.jobSeekerId === jobSeekerId)) {
            //     // engaged before
            //     jsEngagedList.push({
            //         jobId: jobId,
            //         jobSeekerId,
            //         engageDt: now,
            //     });
            // }

            //-------------------------------------------------------------------------------------------
            // write interviews
            //-------------------------------------------------------------------------------------------
            const startDt = getCombinedDateTime(interviewDate, startTime);
            let endDt = getCombinedDateTime(interviewDate, endTime);
            if (endTime < startTime) {
                endDt = addDays(endDt, 1);
            }
            let payload = {
                jobSeekerId,
                jobSeekerName,
                employerId,
                companyName,
                isEmpBadge: tier === 3,
                jobId,
                jobTitle,
                pocNames,
                createDt: now,
                startDt,
                endDt,
                virtual,
                streetAddress1,
                streetAddress2,
                city,
                cityLocation,
                state,
                country,
                postalCode,
                status: INTERVIEW_STATUS_PENDING,
                interviewers: assignInterviewerIds({ interviewers }),
                externalInterviewLink,
                interviewersInvitedBy,
                joinCode,
            };

            if (virtual) {
                payload = removeFromDto(payload, ['streetAddress1', 'streetAddress2', 'city', 'state', 'country', 'postalCode']);
            }

            await transaction.set(interviewRef, deleteEmptyFields(payload), { merge: true });

            // if new engagement, increment jsEngageCount and add new doc to engagements collection
            if (isNewEngagement) {
                //-------------------------------------------------------------------------------------------
                // write employers
                //-------------------------------------------------------------------------------------------
                // await transaction.update(employerRef, { jsEngagedList });
                await transaction.set(employerRef, { jsEngageCount: getFirestoreIncrementField(1) }, { merge: true });

                //-------------------------------------------------------------------------------------------
                // write myJobsEmp and engagements (new)
                //-------------------------------------------------------------------------------------------
                const engagementId = nanoid();
                const engagementRef = myJobsEmpRef.collection('engagements').doc(engagementId);
                await transaction.set(engagementRef, { jsId: jobSeekerId, jobId, engageDt: now, jsName, jobTitle });
            }
        });

        //-------------------------------------------------------------------------------------------
        // update successful
        //-------------------------------------------------------------------------------------------
        callback();
        setSnackbarMessage(SNACK_BAR_MESSAGE_INTERVIEW_SUCCESS);
        return { success: true };
        ////
    } catch (error) {
        switch (error.message) {
            case 'engagement-limit-exceeded':
                return { errorCode: 'engagement-limit-exceeded' };
            default:
                console.log('Failed to save new interview', error);
                setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
                return { errorCode: 'save-interview-failed' };
        }
    }
};

/**
 * @deprecated since 2022-07-12
 */
export const saveJobInterview = async ({
    data = {},
    isNewEngagement,
    jobSeekerId,
    jobSeekerName,
    employerId,
    employer,
    setSnackbarMessage,
    setSnackbarErrorMessage,
    postSaveActions = _noop,
}) => {
    const db = getFirestore();

    //-------------------------------------------------------------------------------------------
    // obtain data
    //-------------------------------------------------------------------------------------------
    const {
        jobId,
        date: interviewDate,
        jobTitle,
        pocNames,
        startTime,
        endTime,
        virtual,
        streetAddress1,
        streetAddress2,
        city,
        state,
        country,
        postalCode,
        interviewers,
        interviewersInvitedBy,
    } = data;
    const { companyName, isEmpBadge, tier } = employer;
    const now = new Date();
    let joinCode = null;

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);

    //-------------------------------------------------------------------------------------------
    // prep for interviews
    //-------------------------------------------------------------------------------------------
    const interviewId = nanoid();
    const interviewRef = db.collection('interviews').doc(interviewId);
    if (interviewers?.length > 0) {
        joinCode = nanoid(10);
    }
    console.log({ joinCode });

    //-------------------------------------------------------------------------------------------
    // prep for myJobsEmp
    //-------------------------------------------------------------------------------------------
    const myJobsEmpRef = db.collection('myJobsEmp').doc(employerId);
    const empJobDocRef = myJobsEmpRef.collection('empJobs').doc(jobId);

    //-------------------------------------------------------------------------------------------
    // start transaction
    //-------------------------------------------------------------------------------------------
    try {
        await db.runTransaction(async transaction => {
            //-------------------------------------------------------------------------------------------
            // read employers
            //-------------------------------------------------------------------------------------------
            const employerDoc = await transaction.get(employerRef);
            const employerData = employerDoc.data();

            // record this engagement if not already exist
            const jsEngagedList = _get(employerData, 'jsEngagedList', []);
            if (!_some(jsEngagedList, o => o.jobId === jobId && o.jobSeekerId === jobSeekerId)) {
                // engaged before
                jsEngagedList.push({
                    jobId: jobId,
                    jobSeekerId,
                    engageDt: now,
                });
            }

            //-------------------------------------------------------------------------------------------
            // write employers
            //-------------------------------------------------------------------------------------------
            await transaction.update(employerRef, { jsEngagedList });

            //-------------------------------------------------------------------------------------------
            // write interviews
            //-------------------------------------------------------------------------------------------
            const startDt = getCombinedDateTime(interviewDate, startTime);
            let endDt = getCombinedDateTime(interviewDate, endTime);
            if (endTime < startTime) {
                endDt = addDays(endDt, 1);
            }
            let payload = {
                jobSeekerId,
                jobSeekerName,
                employerId,
                companyName,
                isEmpBadge: isEmpBadge || tier === 3,
                jobId,
                jobTitle,
                pocNames,
                startDt,
                endDt,
                virtual,
                streetAddress1,
                streetAddress2,
                city,
                state,
                country,
                postalCode,
                status: INTERVIEW_STATUS_PENDING,
                interviewers,
                interviewersInvitedBy,
                joinCode,
                updateDt: now,
            };

            if (virtual) {
                payload = removeFromDto(payload, ['streetAddress1', 'streetAddress2', 'city', 'state', 'country', 'postalCode']);
            }

            await transaction.set(interviewRef, deleteEmptyFields(payload), { merge: true });

            //-------------------------------------------------------------------------------------------
            // write myJobsEmp
            //-------------------------------------------------------------------------------------------
            if (isNewEngagement) {
                await transaction.set(empJobDocRef, { jsEngageCount: getFirestoreIncrementField(1) }, { merge: true });

                const engagementId = nanoid();
                const engagementRef = myJobsEmpRef.collection('engagements').doc(engagementId);
                await transaction.set(engagementRef, { jsId: jobSeekerId, jobId, engageDt: now });
            }

            //-------------------------------------------------------------------------------------------
            // update successful
            //-------------------------------------------------------------------------------------------
            postSaveActions();
            setSnackbarMessage(SNACK_BAR_MESSAGE_INTERVIEW_SUCCESS);
        });
    } catch (error) {
        console.log('Failed to save new interview', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const sendNewExternalThreadMessage = async ({
    messageDto,
    // isNewEngagement,
    activeJobMax,
    jsEngageMax,
    employerId,
    tier,
    companyName,
    companyProfilePicUrl,
    jobSeekerId,
    jobSeekerName,
    jobSeekerProfilePicUrl,
    jobId,
    jobTitle,
    setSnackbarMessage,
    setSnackbarErrorMessage,
    callback = _noop,
}) => {
    // console.log({ messageDto, employerId, employer, jobSeekerId, jobSeekerName, jobSeekerProfilePicUrl, jobId, jobTitle, setSnackbarMessage, setSnackbarErrorMessage });
    const db = getFirestore();

    //-------------------------------------------------------------------------------------------
    // prep data
    //-------------------------------------------------------------------------------------------
    const messageCreateDt = _get(messageDto, 'createDt', new Date());
    // const companyName = _get(employer, 'companyName');
    // const companyProfilePicUrl = _get(employer, 'profilePicUrl');
    // const tier = _get(employer, 'tier');
    const isEmpBadge = tier === 3;
    let isNewThread = false;

    //-------------------------------------------------------------------------------------------
    // prep for thread
    //-------------------------------------------------------------------------------------------
    const threadsRef = db.collection('threads');
    const threadDto = {
        employerId,
        companyName,
        isEmpBadge,
        companyProfilePicUrl,
        jobSeekerId,
        jobSeekerName,
        jobSeekerProfilePicUrl,
        jobId,
        jobTitle,
        // members, // will be injected later
        internal: false,
    };

    //-------------------------------------------------------------------------------------------
    // read threads
    //-------------------------------------------------------------------------------------------
    // attempt to get threadId
    let threadId = null;
    const threadsQuery = threadsRef.where('jobSeekerId', '==', jobSeekerId).where('employerId', '==', employerId).where('jobId', '==', jobId).limit(1);
    const threadsSnapshot = await threadsQuery.get();
    threadsSnapshot.forEach(threadDoc => {
        threadId = threadDoc.id;
    });
    // if not found, it's a new thread, generate thread id
    if (!threadId) {
        threadId = nanoid();
        _set(threadDto, 'createDt', messageCreateDt);
        isNewThread = true;
    }

    const threadRef = threadsRef.doc(threadId);

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);

    //-------------------------------------------------------------------------------------------
    // prep for myJobsEmp
    //-------------------------------------------------------------------------------------------
    const myJobsEmpRef = db.collection('myJobsEmp').doc(employerId);
    // const empJobDocRef = myJobsEmpRef.collection('empJobs').doc(jobId);

    //-------------------------------------------------------------------------------------------
    // prep for threadMessages
    //-------------------------------------------------------------------------------------------
    const threadMessageRef = db.collection('threadMessages').doc(nanoid());

    //-------------------------------------------------------------------------------------------
    // prep for engagements (existing JS)
    //-------------------------------------------------------------------------------------------
    const jsEngagementsQuery = db.collection(`myJobsEmp/${employerId}/engagements`).where('jsId', '==', jobSeekerId).limit(1);

    //-------------------------------------------------------------------------------------------
    // read engagements (existing JS)
    //-------------------------------------------------------------------------------------------
    const jsEngagementsSnapshot = await jsEngagementsQuery.get();
    const isNewEngagement = jsEngagementsSnapshot.size === 0;

    try {
        await db.runTransaction(async transaction => {
            //-------------------------------------------------------------------------------------------
            // read employers
            //-------------------------------------------------------------------------------------------
            const employerDoc = await transaction.get(employerRef);
            if (!employerDoc.exists) throw new Error('The employer document not exists');
            const employerData = employerDoc.data();
            const tier = employerData.tier;
            const billMonthly = employerData.billMonthly;

            // for new engagement, check if employer has already reached the billing cycle limit
            if (isNewEngagement && (tier === 1 || tier === 2)) {
                const jsEngageCount = employerData.jsEngageCount;
                const jsEngageLimit = getTotalEngagementMax({ tier, jsEngageMax, activeJobMax, billMonthly });
                if (jsEngageCount >= jsEngageLimit) {
                    throw new Error('engagement-limit-exceeded');
                }
            }

            // build the employer members to store in the thread doc
            const members = {};
            //? excluding unused fields
            _forEach(employerData.members, member => {
                _set(
                    members,
                    member.userId,
                    mapDefinedFields({
                        name: getNameByUser(member),
                        profilePicUrl: member.profilePicUrl,
                    }),
                );
            });

            //-------------------------------------------------------------------------------------------
            // read employers
            //-------------------------------------------------------------------------------------------
            // const employerDoc = await transaction.get(employerRef);
            // const employerData = employerDoc.data();

            // // record this engagement if not already exist
            // const jsEngagedList = _get(employerData, 'jsEngagedList', []);
            // if (!_some(jsEngagedList, o => o.jobId === jobId && o.jobSeekerId === jobSeekerId)) {
            //     // engaged before
            //     jsEngagedList.push({
            //         jobId: jobId,
            //         jobSeekerId,
            //         engageDt: messageCreateDt,
            //     });
            // }

            // if new engagement, increment jsEngageCount and add new doc to engagements collection
            if (isNewEngagement) {
                //-------------------------------------------------------------------------------------------
                // write employers
                //-------------------------------------------------------------------------------------------
                // await transaction.update(employerRef, { jsEngagedList });
                await transaction.set(employerRef, { jsEngageCount: getFirestoreIncrementField(1) }, { merge: true });

                //-------------------------------------------------------------------------------------------
                // write myJobsEmp engagements (new)
                //-------------------------------------------------------------------------------------------
                const engagementId = nanoid();
                const engagementRef = myJobsEmpRef.collection('engagements').doc(engagementId);
                await transaction.set(engagementRef, { jsId: jobSeekerId, jobId, engageDt: messageCreateDt, jsName: jobSeekerName, jobTitle });
            }

            //-------------------------------------------------------------------------------------------
            // write threads
            //-------------------------------------------------------------------------------------------
            _set(threadDto, 'members', members);
            transaction.set(threadRef, mapDefinedFields(threadDto), { merge: true });

            //-------------------------------------------------------------------------------------------
            // write threadMessages
            //-------------------------------------------------------------------------------------------
            transaction.set(threadMessageRef, mapDefinedFields({ ...messageDto, threadId }));
        });

        //-------------------------------------------------------------------------------------------
        // save successful
        //-------------------------------------------------------------------------------------------
        callback && callback();

        setSnackbarMessage(` Message was sent to ${jobSeekerName || 'Recipient'} successfully`); // Do not implement Undo here!!!
        return { success: true, isNewThread };
        ////
    } catch (error) {
        switch (error.message) {
            case 'engagement-limit-exceeded':
                return { errorCode: 'engagement-limit-exceeded' };
            default:
                setSnackbarErrorMessage(SNACK_BAR_MESSAGE_SEND_FAILED);
                console.error(error);
                return { errorCode: 'save-interview-failed' };
        }
    }
};

/**
 * @deprecated since 2022-07-12
 */
export const sendNewMessage = async ({
    messageDto,
    isNewEngagement,
    employerId,
    employer,
    jobSeekerId,
    jobSeekerName,
    jobSeekerProfilePicUrl,
    jobId,
    jobTitle,
    setSnackbarMessage,
    setSnackbarErrorMessage,
    postSaveActions,
}) => {
    // console.log({ messageDto, employerId, employer, jobSeekerId, jobSeekerName, jobSeekerProfilePicUrl, jobId, jobTitle, setSnackbarMessage, setSnackbarErrorMessage });
    const db = getFirestore();

    //-------------------------------------------------------------------------------------------
    // prep data
    //-------------------------------------------------------------------------------------------
    const messageCreateDt = _get(messageDto, 'createDt', new Date());
    const companyName = _get(employer, 'companyName');
    const companyProfilePicUrl = _get(employer, 'profilePicUrl');
    const tier = _get(employer, 'tier');
    const isEmpBadge = tier === 3;

    // build the employer members to store in the thread doc
    const members = {};
    //? excluding unused fields
    _forEach(_get(employer, 'members', []), member => {
        _set(
            members,
            member.userId,
            mapDefinedFields({
                name: getNameByUser(member),
                profilePicUrl: member.profilePicUrl,
            }),
        );
    });

    //-------------------------------------------------------------------------------------------
    // prep for thread
    //-------------------------------------------------------------------------------------------
    const threadsRef = db.collection('threads');
    const threadDto = {
        employerId,
        companyName,
        isEmpBadge,
        companyProfilePicUrl,
        jobSeekerId,
        jobSeekerName,
        jobSeekerProfilePicUrl,
        jobId,
        jobTitle,
        members,
        internal: false,
    };

    // attempt to get threadId
    let threadId = null;
    const threadsQuery = threadsRef.where('jobSeekerId', '==', jobSeekerId).where('employerId', '==', employerId).where('jobId', '==', jobId).limit(1);
    const threadsSnapshot = await threadsQuery.get();
    threadsSnapshot.forEach(threadDoc => {
        threadId = threadDoc.id;
    });
    // if not found, it's a new thread, generate thread id
    if (!threadId) {
        threadId = nanoid();
        _set(threadDto, 'createDt', messageCreateDt);
    }

    const threadRef = threadsRef.doc(threadId);

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);

    //-------------------------------------------------------------------------------------------
    // prep for myJobsEmp
    //-------------------------------------------------------------------------------------------
    const myJobsEmpRef = db.collection('myJobsEmp').doc(employerId);
    const empJobDocRef = myJobsEmpRef.collection('empJobs').doc(jobId);

    //-------------------------------------------------------------------------------------------
    // prep for threadMessages
    //-------------------------------------------------------------------------------------------
    const threadMessageRef = db.collection('threadMessages').doc(nanoid());

    try {
        await db.runTransaction(async transaction => {
            //-------------------------------------------------------------------------------------------
            // read employers
            //-------------------------------------------------------------------------------------------
            const employerDoc = await transaction.get(employerRef);
            const employerData = employerDoc.data();

            // record this engagement if not already exist
            const jsEngagedList = _get(employerData, 'jsEngagedList', []);
            if (!_some(jsEngagedList, o => o.jobId === jobId && o.jobSeekerId === jobSeekerId)) {
                // engaged before
                jsEngagedList.push({
                    jobId: jobId,
                    jobSeekerId,
                    engageDt: messageCreateDt,
                });
            }

            //-------------------------------------------------------------------------------------------
            // write employers
            //-------------------------------------------------------------------------------------------
            await transaction.update(employerRef, { jsEngagedList });

            //-------------------------------------------------------------------------------------------
            // write myJobsEmp
            //-------------------------------------------------------------------------------------------
            if (isNewEngagement) {
                await transaction.set(empJobDocRef, { jsEngageCount: getFirestoreIncrementField(1) }, { merge: true });

                const engagementId = nanoid();
                const engagementRef = myJobsEmpRef.collection('engagements').doc(engagementId);
                await transaction.set(engagementRef, { jsId: jobSeekerId, jobId, engageDt: messageCreateDt });
            }

            //-------------------------------------------------------------------------------------------
            // write threads
            //-------------------------------------------------------------------------------------------
            transaction.set(threadRef, mapDefinedFields(threadDto), { merge: true });

            //-------------------------------------------------------------------------------------------
            // write threadMessages
            //-------------------------------------------------------------------------------------------
            transaction.set(threadMessageRef, mapDefinedFields({ ...messageDto, threadId }));

            //-------------------------------------------------------------------------------------------
            // save successful
            //-------------------------------------------------------------------------------------------
            if (postSaveActions) postSaveActions();

            setSnackbarMessage(` Message was sent to ${jobSeekerName || 'Recipient'} successfully`); // Do not implement Undo here!!!
        });
    } catch (error) {
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_SEND_FAILED);
        console.error(error);
    }
};

export const uploadThreadAttachment = ({ file, senderId, threadId, setProgress, setIsUploading, onAttachmentUploadComplete }) => {
    const storage = getCloudStorage();

    //-------------------------------------------------------------------------------------------
    // prep data
    //-------------------------------------------------------------------------------------------
    const name = _get(file, 'name');
    const timestampStr = formatDate(new Date(), 'yyyyMMddHHmmss_T');
    const storagePath = `users/${senderId}/threads/${threadId}/${timestampStr}`;
    const uploadTask = storage.ref(`${storagePath}/${name}`).put(file, { customMetadata: { senderId, threadId, key: timestampStr, originFilename: name, isMsgAttachment: true } });

    if (setIsUploading) setIsUploading(true);

    //-------------------------------------------------------------------------------------------
    // upload file
    //-------------------------------------------------------------------------------------------
    uploadTask.on(
        'state_changed',
        snapshot => {
            // progress function ...
            if (setProgress) {
                const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                setProgress(progress);
            }
        },
        error => {
            // Error function ...
            console.error('File upload failed', error);
        },
        async () => {
            // complete function ...
            try {
                const url = await storage.ref(storagePath).child(name).getDownloadURL();
                onAttachmentUploadComplete(file, url);
            } catch (error) {
                console.error('Failed to obtain file URL:', error);
            }
        },
    );
};

export const getJobSeekerProfilePicUrl = async ({ userId, set }) => {
    const db = getFirestore();
    const userRef = db.collection('users').doc(userId);

    const doc = await userRef.get();
    if (doc.exists) {
        const { profilePicUrl } = doc.data();
        set(profilePicUrl);
    }
};

export const sendNewInternalMessage = async ({
    messageDto,
    employerId,
    employer,
    subject,
    setSnackbarMessage,
    setSnackbarErrorMessage,
    postSaveActions,
    recipients,
    senderMember,
    internal,
}) => {
    const db = getFirestore();

    //-------------------------------------------------------------------------------------------
    // prep data
    //-------------------------------------------------------------------------------------------
    const messageCreateDt = _get(messageDto, 'createDt', new Date());
    const companyName = _get(employer, 'companyName');
    const companyProfilePicUrl = _get(employer, 'profilePicUrl');
    const tier = _get(employer, 'tier');
    const isEmpBadge = tier === 3;

    // build the employer members to store in the thread doc
    const memberstoSend = {};
    const members = [...recipients, senderMember];
    _forEach(members, member => {
        _set(
            memberstoSend,
            member.userId,
            mapDefinedFields({
                name: getNameByUser(member),
                profilePicUrl: member.profilePicUrl,
            }),
        );
    });

    //-------------------------------------------------------------------------------------------
    // prep for thread
    //-------------------------------------------------------------------------------------------
    const threadsRef = db.collection('threads');
    const threadDto = {
        employerId,
        companyName,
        isEmpBadge,
        companyProfilePicUrl,
        subject,
        members: memberstoSend,
        internal,
        createDt: messageCreateDt, //assuming this is always a new thread
        starterId: senderMember?.userId,
        starterName: `${senderMember?.firstName} ${senderMember?.lastName}`,
        starterProfilePicUrl: senderMember?.profilePicUrl,
    };

    let threadId = nanoid();

    const threadRef = threadsRef.doc(threadId);

    //-------------------------------------------------------------------------------------------
    // prep for threadMessages
    //-------------------------------------------------------------------------------------------
    const threadMessageRef = db.collection('threadMessages').doc(nanoid());

    try {
        await db.runTransaction(async transaction => {
            //-------------------------------------------------------------------------------------------
            // write threads
            //-------------------------------------------------------------------------------------------
            transaction.set(threadRef, mapDefinedFields(threadDto), { merge: true });

            //-------------------------------------------------------------------------------------------
            // write threadMessages
            //-------------------------------------------------------------------------------------------
            transaction.set(threadMessageRef, mapDefinedFields({ ...messageDto, threadId }));

            //-------------------------------------------------------------------------------------------
            // save successful
            //-------------------------------------------------------------------------------------------
            if (postSaveActions) postSaveActions();

            setSnackbarMessage(` Message was sent successfully`); // Do not implement Undo here!!!
        });
    } catch (error) {
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_SEND_FAILED);
        console.error(error);
    }
};

export const getCurrentBillingPeriodEngagements = ({ employerId, billCycleStartDt, setList }) => {
    const db = getFirestore();
    const jsEngagementsQuery = db.collection(`myJobsEmp/${employerId}/engagements`).where('engageDt', '>', billCycleStartDt).limit(200);

    let engagements = [];
    return jsEngagementsQuery.onSnapshot(docSnapshot => {
        engagements = docSnapshot.docs.map(doc => {
            let data = doc.data();
            return convertFirebaseTimestampsToDate({ ...data, uid: doc.id }, ['engageDt']);
        });
        return setList(engagements);
    });
};

export const updateMyProfileReminderDts = async ({ userId, employerId, isShowWalkThrough, isShowFirstTimeShareTourStep, isShowMakePublicReminderAgain } = {}) => {
    const db = getFirestore();

    const now = new Date();
    const ref = db.collection('dashboards').doc(`${userId}${COMPOSITE_DOC_ID_DELIMITER}${employerId}`);

    try {
        // Update Dashboard document
        await db.runTransaction(async transaction => {
            // This code may get re-run multiple times if there are conflicts.
            const dto = {};
            if (isShowWalkThrough) {
                // user has seen the complete walk-through
                _set(dto, 'showMakeProfilePublicTourStepDt', now);
                _set(dto, 'showShareProfileTourStepDt', now);
            } else if (isShowMakePublicReminderAgain) {
                // user has seen the make-profile-public reminder
                _set(dto, 'showMakeProfilePublicTourStepDt', now);
            } else if (isShowFirstTimeShareTourStep) {
                // user has seen the share-profile reminder
                _set(dto, 'showMakeProfilePublicTourStepDt', now); // assuming he has a public profile already when we release the walk-through update
                _set(dto, 'showShareProfileTourStepDt', now);
            }

            transaction.set(
                ref,
                {
                    ...dto,
                    updateDt: new Date(),
                },
                { merge: true },
            );
        });
    } catch (error) {
        console.error('Save My Profile reminder timestamps transaction failed: ', error);
    }
};
